/**
 * The KANBAN view's own Watchlist-parity filter panel (SE-7405).
 *
 * Mirrors `MainContainer.tsx`'s Table-view filter block (SE-7144)
 * field-for-field, extracted to its own hook so that block's line count
 * doesn't also count against `MainContainer`'s already-tight
 * `max-lines-per-function` ratchet (`eslint.thresholds.json`, monotonically
 * decreasing — bumping it is the wrong lever here).
 *
 * Narrows the KANBAN BOARD itself, unlike the legacy FilterModal/boardFilters
 * pair, which only ever scoped the add-players rail's pool. QA found that gap
 * (Filters narrowed the Table but not the Kanban board) alongside the visual
 * mismatch this also closes, since both views now share the identical
 * `AddPlayersFilterPanel`-backed drawer (rendered via `TableFilterDrawer`,
 * which is a thin, board-agnostic shell — nothing about it is actually
 * Table-specific).
 * @module features/player-kanban/hooks/useKanbanBoardFilters
 */

import { useCallback, useMemo, useState } from "react";

import type {
  PlayerAdvancedFilterInput,
  KanbanCard,
} from "@/generated/graphql";
import useAdvancedFilterState from "@/hooks/useFilterState";
import useFilterVariables from "@/features/scouting/hooks/useFilterVariables";

import type { ColumnData } from "../types/types";
import type { BoardPlayerMetrics } from "../utils/playerFilter";
import {
  applyTableFilterPassSet,
  buildTableFilterPassSet,
} from "../utils/tableAdvancedFilter";

/** A single LISTS chip option — a watchlist the board's players belong to. */
export interface KanbanFilterListOption {
  readonly id: string;
  readonly label: string;
}

/** Public API returned by {@link useKanbanBoardFilters}. */
export interface UseKanbanBoardFiltersResult<
  T extends ColumnData[] | undefined,
> {
  /** The committed advanced filter state, owned by the shared scouting hook. */
  readonly filterState: ReturnType<typeof useAdvancedFilterState>;
  /** Whether the Kanban Filters drawer is open. */
  readonly isFilterOpen: boolean;
  /** Opens the Kanban Filters drawer. */
  readonly onFiltersOpen: () => void;
  /** Closes the Kanban Filters drawer. */
  readonly onFiltersClose: () => void;
  /** The committed LISTS selection — watchlist ids. */
  readonly listSelection: readonly string[];
  /** The LISTS chip options — watchlists the board's players actually belong to. */
  readonly listOptions: readonly KanbanFilterListOption[];
  /** Commits a new LISTS selection. */
  readonly onCommitLists: (selection: readonly string[]) => void;
  /**
   * `visibleColumnsData`, narrowed by the committed filters — the same
   * definedness as the `visibleColumnsData` argument (matching
   * `applyTableFilterPassSet`'s own generic signature), so a caller holding a
   * definite `ColumnData[]` gets one back, not a widened `| undefined`.
   */
  readonly parityColumnsData: T;
  /** Total committed filter count, for the toolbar's Filters badge. */
  readonly activeFilterCount: number;
}

/**
 * Drive the Kanban view's Watchlist-parity filter panel and narrow the
 * board's own rendered columns by its committed state.
 * @param visibleColumnsData - The Kanban board's columns as rendered (search/sort applied).
 * @param boardCards - The board's raw cards (same array Table's parity block reads).
 * @param boardPlayerMetricsById - SE-6871 metrics sidecar, keyed by player id.
 * @returns The {@link UseKanbanBoardFiltersResult}.
 */
export function useKanbanBoardFilters<T extends ColumnData[] | undefined>(
  visibleColumnsData: T,
  boardCards: readonly KanbanCard[] | undefined,
  boardPlayerMetricsById: ReadonlyMap<string, BoardPlayerMetrics> | undefined
): UseKanbanBoardFiltersResult<T> {
  const filterState = useAdvancedFilterState();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [listSelection, setListSelection] = useState<readonly string[]>([]);

  const onFiltersOpen = useCallback(() => setIsFilterOpen(true), []);
  const onFiltersClose = useCallback(() => setIsFilterOpen(false), []);

  const advancedInput = useFilterVariables(
    filterState,
    ""
  ) as PlayerAdvancedFilterInput;

  const listOptions = useMemo(() => {
    const entries = (boardCards ?? []).flatMap(card =>
      (card.player?.watchlists ?? []).flatMap(watchlist =>
        watchlist ? [[watchlist.id, watchlist.name] as const] : []
      )
    );
    return Object.entries(Object.fromEntries(entries))
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [boardCards]);

  const onCommitLists = useCallback(
    (selection: readonly string[]) => setListSelection(selection),
    []
  );

  const filterPassSet = useMemo(
    () =>
      buildTableFilterPassSet({
        players: (boardCards ?? []).flatMap(card =>
          card.player ? [card.player] : []
        ),
        metricsById: boardPlayerMetricsById,
        advancedInput,
        listSelection,
        activeFilterCount: filterState.activeFilterCount,
      }),
    [
      boardCards,
      boardPlayerMetricsById,
      advancedInput,
      listSelection,
      filterState.activeFilterCount,
    ]
  );

  // `applyTableFilterPassSet` is board-agnostic (narrows any `ColumnData[]` by
  // player id + refreshes each column's count) — Kanban's own columns are the
  // SAME shape Table's are, just grouped by stage instead of flattened to
  // rows, so the identical utility applies unchanged.
  const parityColumnsData = useMemo(
    () => applyTableFilterPassSet(visibleColumnsData, filterPassSet),
    [visibleColumnsData, filterPassSet]
  );

  const activeFilterCount =
    filterState.activeFilterCount + (listSelection.length > 0 ? 1 : 0);

  return {
    filterState,
    isFilterOpen,
    onFiltersOpen,
    onFiltersClose,
    listSelection,
    listOptions,
    onCommitLists,
    parityColumnsData,
    activeFilterCount,
  };
}
